/**
 * In-App Purchases via RevenueCat (@revenuecat/purchases-capacitor).
 *
 * iOS + Android native — web uses Stripe (/pricing).
 * The SDK is imported dynamically so the web bundle never touches StoreKit
 * or Google Play Billing.
 *
 * Identity: we always configure with our own backend user id as the
 * RevenueCat appUserID, so webhooks map 1:1 onto our users collection.
 */
import { isAndroidNative, isIOSNative, isNativeWithIAP } from "@/lib/platform";

/** Apple's own subscription-management page — the only external link Apple
 *  permits for managing/cancelling an IAP subscription. */
export const APPLE_MANAGE_SUBSCRIPTIONS_URL = "https://apps.apple.com/account/subscriptions";

/** Google Play subscription management page — same purpose for Android. */
export const PLAY_MANAGE_SUBSCRIPTIONS_URL = "https://play.google.com/store/account/subscriptions";

export const PAYWALL_OPEN_EVENT = "bm:open-paywall";

/** Open the native paywall sheet from anywhere (Dashboard badge, banner, modal…). */
export function openPaywall() {
    window.dispatchEvent(new CustomEvent(PAYWALL_OPEN_EVENT));
}

let configured = false;
let configureError = ""; // human-readable reason why configure failed (debug aid)
let configuredUserId = null; // last appUserID we handed to RevenueCat
// Holds the in-flight configure()/logIn() call so purchase/restore/offerings
// can await it — prevents a fast tap from firing a purchase against
// $RCAnonymousID before the appUserID is registered.
let configurePromise = null;

async function sdk() {
    // Return the module namespace, NOT mod.Purchases directly: the Capacitor
    // plugin proxy intercepts every property access — including `.then` that
    // `await` probes for — and throws "not implemented on web".
    return import("@revenuecat/purchases-capacitor");
}

/** Best-effort push of email/displayName to the RC dashboard. Non-fatal. */
async function applyIdentityAttributes(Purchases, user) {
    if (!user || typeof user !== "object") return;
    const email = user.email;
    const displayName = user.name || user.full_name || user.display_name;
    try {
        if (email) await Purchases.setEmail({ email: String(email) });
        if (displayName) await Purchases.setDisplayName({ displayName: String(displayName) });
    } catch (attrErr) {
        console.warn("[iap] setEmail/setDisplayName failed (non-fatal)", attrErr);
    }
}

/**
 * Configure RevenueCat with the current captain.
 * Accepts either the legacy plain userId string or the full user object.
 * When a user object is passed, we also populate `$email` / `$displayName`
 * so the RevenueCat dashboard shows real identities for support.
 *
 * Safe to call multiple times: if the userId changes (e.g. captain A logs out
 * and captain B logs in on the same device without an app restart), we call
 * Purchases.logIn({appUserID}) to switch identities cleanly.
 */
export async function configurePurchases(user) {
    if (!isNativeWithIAP() || !user) return;
    const userId = typeof user === "string" ? user : user?.id;
    if (!userId) return;

    // Already configured for THIS user — nothing to do.
    if (configured && configuredUserId === userId) return;

    // A configure/switch is already in flight — reuse its promise.
    if (configurePromise) return configurePromise;

    // Already configured for a DIFFERENT user (shared device) — switch identity.
    if (configured && configuredUserId && configuredUserId !== userId) {
        configurePromise = (async () => {
            try {
                const { Purchases } = await sdk();
                await Purchases.logIn({ appUserID: userId });
                configuredUserId = userId;
                await applyIdentityAttributes(Purchases, user);
            } catch (e) {
                console.error("[iap] RevenueCat logIn failed", e);
            } finally {
                configurePromise = null;
            }
        })();
        return configurePromise;
    }

    // First configure of this session.
    configurePromise = (async () => {
        // RevenueCat uses a different API key per platform — pick the right one.
        const apiKey = isIOSNative()
            ? process.env.REACT_APP_RC_IOS_KEY
            : process.env.REACT_APP_RC_ANDROID_KEY;
        if (!apiKey) {
            const which = isIOSNative() ? "REACT_APP_RC_IOS_KEY" : "REACT_APP_RC_ANDROID_KEY";
            configureError = "billing key missing from this build";
            console.warn(`[iap] ${which} missing — native paywall disabled`);
            return;
        }
        try {
            const { Purchases } = await sdk();
            await Purchases.configure({ apiKey, appUserID: userId });
            configured = true;
            configuredUserId = userId;
            configureError = "";
            await applyIdentityAttributes(Purchases, user);
        } catch (e) {
            configureError = e?.message || "billing setup failed";
            console.error("[iap] RevenueCat configure failed", e);
        }
    })();

    return configurePromise;
}

/**
 * Called from AuthContext on logout. Signs the current captain out of
 * RevenueCat so a subsequent login on the same device gets a clean slate.
 * Safe on web (short-circuits) and safe to call before configure() ran.
 */
export async function resetPurchases() {
    if (!isNativeWithIAP()) return;
    // Wait for any in-flight configure so we don't logOut mid-configure.
    if (configurePromise) {
        try { await configurePromise; } catch { /* ignore */ }
    }
    if (!configured) return;
    try {
        const { Purchases } = await sdk();
        await Purchases.logOut();
    } catch (e) {
        // logOut throws if no anonymous user is left to switch to — non-fatal.
        console.warn("[iap] RevenueCat logOut failed (non-fatal)", e);
    } finally {
        configured = false;
        configuredUserId = null;
        configurePromise = null;
        // Keep configureError so a missing key is still reported next time.
    }
}

/** Returns { monthly, annual } packages from the current offering. */
export async function getPlanPackages() {
    if (configurePromise) await configurePromise; // race guard
    if (!configured && configureError) {
        throw new Error(configureError);
    }
    const { Purchases } = await sdk();
    const offerings = await Purchases.getOfferings();
    const pkgs = offerings?.current?.availablePackages || [];
    return {
        monthly: pkgs.find((p) => p.packageType === "MONTHLY") || null,
        annual: pkgs.find((p) => p.packageType === "ANNUAL") || null,
    };
}

export async function purchase(pkg) {
    if (configurePromise) await configurePromise; // race guard
    const { Purchases } = await sdk();
    const res = await Purchases.purchasePackage({ aPackage: pkg });
    return res?.customerInfo || res;
}

export async function restore() {
    if (configurePromise) await configurePromise; // race guard
    const { Purchases } = await sdk();
    const res = await Purchases.restorePurchases();
    return res?.customerInfo || res;
}

export function hasProEntitlement(customerInfo) {
    return !!customerInfo?.entitlements?.active?.pro;
}
